import { EChartsOption } from 'echarts';

/**
 * Changes the chart type of an EChartsOption object. Assumes every series in the option object has the same chart type.
 *
 * @param targetType - The target chart type. Can be either 'line' or 'bar'.
 * @param option - The EChartsOption object to modify.
 *
 * @returns A new EChartsOption object with the chart type changed to the target type.
 *
 * @throws {Error} If option.series is not defined or not an array.
 */
export function changeSeriesType(
    targetSeriesType: 'line' | 'bar',
    option: EChartsOption
): EChartsOption {
    // copy the option object
    const newOption = { ...option };

    // raise error if option.series is not defined
    if (!newOption.series) {
        throw new Error('Option.series is not defined');
    }
    // raise error if option.series is not an array
    if (!Array.isArray(newOption.series)) {
        throw new Error('Option.series is not an array');
    }
    // Update the chart type in the series
    newOption.series.forEach((series) => {
        series.type = targetSeriesType;
    });

    return newOption;
}

/**
 * Gets the chart type of an EChartsOption object. Assumes every series in the option object has the same chart type.
 *
 * @param option - The EChartsOption object to inspect.
 *
 * @returns The chart type of the EChartsOption object. Can be either 'line' or 'bar'.
 *
 * @throws {Error} If option.series is not an array, if there is more than one series type, or if the series type is not 'line' or 'bar'.
 */
export function getSeriesType(option: EChartsOption): 'line' | 'bar' {
    // get unique series types
    // throw error if series is not array
    if (!Array.isArray(option.series)) {
        throw new Error('Option.series is not an array');
    }
    const seriesTypes = new Set(option.series?.map((s) => s.type));
    // throw error if more than 1 type
    if (seriesTypes.size > 1) {
        throw new Error('More than 1 series type');
    }

    if (!seriesTypes.has('line') && !seriesTypes.has('bar')) {
        throw new Error('Series type is not line or bar');
    }

    const seriesType = seriesTypes.values().next().value;

    // throw error if series type is not line or bar
    if (seriesType !== 'line' && seriesType !== 'bar') {
        throw new Error('Series type is not line or bar');
    }

    return seriesType;
}
